import {
  cloneListOrNull, DataModelBuilder, isSameList, SerializationContext, SerializedData,
} from '../../models/dto/dto.js';

import dayjs from 'dayjs';
import { MapUtils } from '../../utility/map-utils.js';
import { Component } from '../core/component.js';
import { TimeZone } from '../../time/timezone.js';
import { VersionRange } from '../../models/core/version-range.js';
import { fromEnumAsStringOrNull, toEnumFromStringOrNull } from '../../serialization/enum-utils.js';
import { Price } from '../../models/serializable/price.js';
import { OpenTicketPassType } from '../../models/serializable/discounts/open-ticket-pass-type.js';

export class OpenTicketPassComponent extends Component {
  static readonly TYPE_ID: string = '20898ec9e27f47ada28bb4e198e084a7';
  static readonly SCHEMA_VERSION: number = 6;

  private constructor(
    public readonly includedEvents: string[] | null,
    public readonly excludedEvents: string[] | null,
    public readonly ticketId: string[] | null,
    public readonly maxUses: number,
    public readonly startDate: dayjs.Dayjs | null,
    public readonly expirationDate: dayjs.Dayjs | null,
    public readonly type: OpenTicketPassType | null,
  ) {
    super();
  }

  get $removable(): boolean {
    return false;
  }

  get $typeId(): string {
    return OpenTicketPassComponent.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(5, OpenTicketPassComponent.SCHEMA_VERSION);
  }

  static create(args: {
    includedEvents: string[] | null,
    excludedEvents: string[] | null,
    ticketId: string[] | null,
    maxUses: number,
    startDate: dayjs.Dayjs | null,
    expirationDate: dayjs.Dayjs | null,
    type: OpenTicketPassType,
  }): OpenTicketPassComponent {
    return new OpenTicketPassComponent(
        args.includedEvents,
        args.excludedEvents,
        args.ticketId,
        args.maxUses,
        args.startDate,
        args.expirationDate,
        args.type,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): OpenTicketPassComponent {
    return new OpenTicketPassComponent(
        MapUtils.getListOrNull<string>(map, 'includedEvents', (e) => e as string),
        MapUtils.getListOrNull<string>(map, 'excludedEvents', (e) => e as string),
        MapUtils.getListOrNull<string>(map, 'ticketId', (e) => e as string),
        MapUtils.get<number>(map, 'maxUses'),
        TimeZone.decodeDateOrNull(MapUtils.getOrNull<number>(map, 'startDate')),
        TimeZone.decodeDateOrNull(MapUtils.getOrNull<number>(map, 'expirationDate')),
        toEnumFromStringOrNull(MapUtils.getStringOrNull(map, 'type'), OpenTicketPassType),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'includedEvents': this.includedEvents,
      'excludedEvents': this.excludedEvents,
      'ticketId': this.ticketId,
      'maxUses': this.maxUses,
      'startDate': TimeZone.encodeDateOrNull(this.startDate),
      'expirationDate': TimeZone.encodeDateOrNull(this.expirationDate),
      'type': fromEnumAsStringOrNull(this.type),
    };
  }

  clone(): this {
    return new OpenTicketPassComponent(
        cloneListOrNull(this.includedEvents),
        cloneListOrNull(this.excludedEvents),
        cloneListOrNull(this.ticketId),
        this.maxUses,
        TimeZone.cloneDateOrNull(this.startDate),
        TimeZone.cloneDateOrNull(this.expirationDate),
        this.type,
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return isSameList(other.includedEvents, this.includedEvents) &&
        isSameList(other.excludedEvents, this.excludedEvents) &&
        isSameList(other.ticketId, this.ticketId) &&
        other.startDate === this.startDate &&
        other.expirationDate === this.expirationDate &&
        other.maxUses === this.maxUses &&
        other.type === this.type;
  }
}

export class OpenTicketPassComponentBuilder extends DataModelBuilder<OpenTicketPassComponent> {
  build(context: SerializationContext, map: SerializedData): OpenTicketPassComponent {
    return OpenTicketPassComponent.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(OpenTicketPassComponent.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(OpenTicketPassComponent.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version we added `expirationDate` of type `dayjs.Dayjs | null`
    if(version < 2) version = 2;

    // In this version we added `type` of type `OpenTicketPassType`
    if(version < 3) {
      const productPrice = context.storage.get<Price>('productPrice');
      if(productPrice != null) {
        let type = OpenTicketPassType.Standard;
        if(productPrice.value <= 0) {
          type = OpenTicketPassType.Protocol;
        }

        map['type'] = fromEnumAsStringOrNull(type);
      }

      version = 3;
    }

    /**
     * Version 4:
     *  - parameter `startDate` type {@link dayjs.Dayjs | null} introduced.
     */
    if(version < 4) version = 4;

    if(version < 5) {
      map['includedEvents'] = map['eventId'];
      version = 5;
    }

    if(version < 6) version = 6;

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
